import { isServerSide } from 'helpers/ssr';
import { generateStyleFlexUrl } from '@common/Product/helpers/styleflex';
import updateQueryParams from './updateQueryParams';
// Created check for serverside rendering
const APPCONFIG = typeof window !== 'undefined' && window.App && window.App.Config ? window.App.Config : {};

/**
 * Generate product url
 *
 * @param {object} product
 * @param {object} params
 * @return {string}
 */
export const generateProductUrl = (product, params = {}, config = {}) => {
    const isCompleteUrl = /^https?:\/\//.test(product.url);

    if (isCompleteUrl) {
        return updateQueryParams(product.url, params);
    }

    const { store = APPCONFIG.store, language = APPCONFIG.language } = config;

    const url = [store, language, product.brand_slug, `${product.product_slug}_${product.product_id}`, product.ean_id].join('/');

    return `/${updateQueryParams(url, params)}`;
};

const getRootURL = (localImageSrc) => {
    if (localImageSrc) {
        return localImageSrc.from;
    }

    return APPCONFIG.LocalImageSrc ? APPCONFIG.LocalImageSrc.from : '';
};

/**
 * Generate product image url
 *
 * @param product
 * @param size
 * @param config
 * @param isGrey
 * @return {string}
 */
export const generateImageUrl = (product, size = '183x239', config, isGrey = false) => {
    const { filename, brand_slug: brandSlug } = product;
    const isCompleteUrl = /^https?:\/\//.test(filename);
    const localImageSrc = config && config.localImageSrc ? config.localImageSrc : APPCONFIG.LocalImageSrc;

    if (isCompleteUrl) {
        return filename;
    }

    const URLParts = [getRootURL(localImageSrc), brandSlug, size];

    if (isGrey) {
        URLParts.push('g');
    }

    URLParts.push(filename);

    return URLParts.join('/');
};

/**
 *
 * @param product
 * @param config
 * @param isGrey
 * @returns {string[]}
 */

export const generateSrcSet = (product, config, isGrey = false) => {
    const sizes = [
        { size: '232x303', width: '232w' },
        { size: '400x523', width: '400w' }
    ];

    return sizes.map(({ size, width }) => `${generateImageUrl(product, size, config, isGrey)} ${width}`).join(', ');
};

export const SIZES = '(max-width: 959px) 163px,'
    + '(max-width: 1023px) 218px,'
    + '(max-width: 1279px) 240px,'
    + '(max-width: 1439px) 250px,'
    + '(max-width: 1599px) 304px,'
    + '(max-width: 1919px) 344px,'
    + '(max-width: 1920px) 450px';

/**
 * Get query URL parameter by string name
 *
 * @param {string} name
 * @param {string} url
 * @returns {string}
 */
export const getUrlParameter = (name, url = null) => {
    if (isServerSide()) {
        return '';
    }

    const myName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]').toLowerCase();
    const regex = new RegExp(`[\\?&]${myName}=([^&#]*)`);
    const results = regex.exec(url || window.location.search.toLowerCase());

    return results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));
};

/**
 * Generate product resizable image url
 *
 * @param {string} filename
 * @param {string} brandSlug
 * @param {string} imageResizeUrl
 * @param {object} params
 * @param {object|null} styleFlx
 * @return {string}
 */
export const getResizableProductImageUrl = ({
    filename,
    brandSlug,
    imageResizeUrl,
    params,
    styleFlex = null
}) => {
    const baseName = filename.substr(0, filename.lastIndexOf('.')) || filename;
    const url = `${imageResizeUrl}/${brandSlug}/${baseName}.webp`;
    const updatedUrl = updateQueryParams(url, params);

    if (styleFlex?.enabled) {
        return generateStyleFlexUrl(styleFlex, updatedUrl, params.size, params?.dpr);
    }

    return updatedUrl;
};

/**
 * Generate product resizable image srcSet
 *
 * @param {string} filename
 * @param {string} brandSlug
 * @param {string} imageResizeUrl
 * @param {number} hasGrey
 * @param {number} hasWebp
 * @return {string}
 */
export const generateSrcSetResizable = ({
    filename,
    brandSlug,
    imageResizeUrl,
    hasGrey,
    hasWebp
}) => {
    const sizes = [
        { size: 'w232', width: '232w' },
        { size: 'w400', width: '400w' }
    ];

    return sizes.map(({ size, width }) => `${getResizableProductImageUrl({
        filename,
        brandSlug,
        imageResizeUrl,
        params: {
            size,
            has_grey: hasGrey,
            has_webp: hasWebp
        }
    })} ${width}`).join(', ');
};
